import { shouldSkipObject } from '@/threejs/objectManager';
import * as THREE from 'three';
import { FullScreenQuad, Pass } from 'three/examples/jsm/postprocessing/Pass.js';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader.js';
import type { Projector } from './projector';
import type { ProjectorFrustumOverlap, ProjectorFrustumOverlapMode } from './projector-frustum-overlap';

export class FrustumOverlapPass extends Pass {
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.Camera;
  private readonly frustumOverlap: ProjectorFrustumOverlap;
  private readonly copyMaterial: THREE.ShaderMaterial;
  private readonly fsQuad: FullScreenQuad;
  private projectors: Projector[];
  private overlayEnabled = false;
  private overlapMode: ProjectorFrustumOverlapMode = 'only';

  constructor(
    scene: THREE.Scene,
    camera: THREE.Camera,
    frustumOverlap: ProjectorFrustumOverlap,
    projectors: Projector[]
  ) {
    super();
    this.scene = scene;
    this.camera = camera;
    this.frustumOverlap = frustumOverlap;
    this.projectors = projectors;
    this.clear = false;
    this.needsSwap = false;
    this.copyMaterial = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.clone(CopyShader.uniforms),
      vertexShader: CopyShader.vertexShader,
      fragmentShader: CopyShader.fragmentShader,
      depthTest: false,
      depthWrite: false,
    });
    this.copyMaterial.blending = THREE.NoBlending;
    this.fsQuad = new FullScreenQuad(this.copyMaterial);
  }

  setProjectors(projectors: Projector[]) {
    this.projectors = projectors;
  }

  setEnabled(enabled: boolean) {
    this.overlayEnabled = enabled;
    this.enabled = enabled;
  }

  getEnabled() {
    return this.enabled;
  }

  setOverlapMode(mode: ProjectorFrustumOverlapMode) {
    this.overlapMode = mode;
  }

  render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget, readBuffer: THREE.WebGLRenderTarget) {
    const previousAutoClear = renderer.autoClear;
    const previousOverlapMode: ProjectorFrustumOverlapMode = this.frustumOverlap.getOverlapMode();
    const previousOverrideMaterial = this.scene.overrideMaterial;
    const previousBackground = this.scene.background;
    renderer.autoClear = false;

    try {
      renderer.setRenderTarget(readBuffer);

      const visibleProjectors = this.projectors.filter((p) => p.visible);
      if (this.overlayEnabled && visibleProjectors.length > 0) {
        // Don't clear depth - we need scene depth to occlude the overlay.

        this.scene.overrideMaterial = this.frustumOverlap.getOverrideMaterial();

        this.frustumOverlap.setOverlapMode('only');
        const batchSize = this.frustumOverlap.getBatchSize();
        const overlayAutoClear = renderer.autoClear;
        renderer.autoClear = false;
        this.frustumOverlap.beginOverlapPass();

        // Avoid background force-clear while layering overlays.
        this.scene.background = null;

        const hiddenObjects: THREE.Object3D[] = [];
        this.scene.traverse((object: THREE.Object3D) => {
          if (object.visible && shouldSkipObject(object)) {
            object.visible = false;
            hiddenObjects.push(object);
          }
        });

        for (let i = 0; i < visibleProjectors.length; i += batchSize) {
          const batch = visibleProjectors.slice(i, i + batchSize);
          this.frustumOverlap.update(renderer, this.scene, batch, {
            overlapOnly: true,
          });
          renderer.render(this.scene, this.camera);
        }

        hiddenObjects.forEach((object) => (object.visible = true));

        this.frustumOverlap.endOverlapPass();
        renderer.autoClear = overlayAutoClear;
      }
    } finally {
      this.scene.background = previousBackground;
      this.scene.overrideMaterial = previousOverrideMaterial;

      this.frustumOverlap.setOverlapMode(previousOverlapMode);
      renderer.autoClear = previousAutoClear;
    }

    if (this.renderToScreen) {
      this.copyMaterial.uniforms['tDiffuse'].value = readBuffer.texture;
      renderer.setRenderTarget(null);
      this.fsQuad.render(renderer);
    }
  }
}
